import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useNavigate } from 'react-router';
import { useEgitimAc, useEgitimOptions } from '../api/queries';
import { EGITIM_YONTEM_ETIKET } from '../i18n/tr';
import { gunGirdisiIso } from '../lib/format';
import { KatilimciSecici } from './egitim-parts';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';

/**
 * Eğitim kaydı açma (F3B·S7 — T1: KAYIT ASILDIR, form değil).
 *
 * Ekran kural YAZMAZ, taşır: şemsiye kalem havuzda HİÇ YOKTUR (sunucu
 * süzer — C.1), süre uyarıları kaydın ardından SUNUCUDAN gelir (EK.1 —
 * uyarı, engel değil), katılımcı TARAFI yüklenici istihdamından OTOMATİK
 * seçilir (T2; ekran ikinci istihdam sorgusu atmaz).
 *
 * EĞİTİCİ İKİ KOLLU (E1): iç kişi referansı XOR dış kurum+ad — "tek düz
 * yazı alanına gömme" yasağının ekran karşılığı; kurum ve ad AYRI alandır.
 */
export function EgitimKayitScreen(): React.JSX.Element {
  const options = useEgitimOptions();
  const ac = useEgitimAc();
  const navigate = useNavigate();

  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [turId, setTurId] = useState<string | null>(null);
  const [tarih, setTarih] = useState('');
  const [sure, setSure] = useState<number | string>(8);
  const [yontem, setYontem] = useState<string>('yuz_yuze');
  const [disEgitici, setDisEgitici] = useState(true);
  const [egiticiPersonId, setEgiticiPersonId] = useState<string | null>(null);
  const [egiticiAd, setEgiticiAd] = useState('');
  const [egiticiKurum, setEgiticiKurum] = useState('');
  // İ4 (tur 5) — dış eğitici TC'si, İSTEĞE BAĞLI; maske ve algoritma SUNUCUDA.
  const [egiticiKimlikNo, setEgiticiKimlikNo] = useState('');
  // İ3 — sorumlu İSG profesyoneli; boş bırakılırsa sunucu kaydı açanı yazar.
  const [sorumluPersonId, setSorumluPersonId] = useState<string | null>(null);
  // İ1 — İBYS referans listeleri (boş dağıtılır; kalem yoksa alan çizilmez).
  const [egitimYeriRefId, setEgitimYeriRefId] = useState<string | null>(null);
  const [sertifikaTipiRefId, setSertifikaTipiRefId] = useState<string | null>(null);
  const [notlar, setNotlar] = useState('');
  const [konular, setKonular] = useState<string[]>([]);
  const [katilimcilar, setKatilimcilar] = useState<string[]>([]);

  const secilenTur = useMemo(
    () => options.data?.egitimTurleri.find((t) => t.id === turId) ?? null,
    [options.data, turId],
  );
  const yukleniciler = useMemo(
    () => new Set(options.data?.yukleniciPersonIds ?? []),
    [options.data],
  );

  const hazir =
    workplaceId !== null &&
    turId !== null &&
    gunGirdisiIso(tarih) !== null &&
    Number(sure) > 0 &&
    (disEgitici ? egiticiAd.trim() !== '' : egiticiPersonId !== null);

  return (
    <Stack>
      <Title order={3}>Yeni eğitim kaydı</Title>
      <QueryStatus queries={[options]} />
      {options.isSuccess && (
        <Card withBorder>
          <Stack gap="sm">
            <Select
              label="İşyeri"
              required
              data-testid="egitim-isyeri"
              data={options.data.workplaces.map((w) => ({
                value: w.id,
                label: `${w.ad} (${w.companyAd})`,
              }))}
              value={workplaceId}
              onChange={setWorkplaceId}
            />
            <Select
              label="Eğitim türü"
              required
              searchable
              data-testid="egitim-tur"
              data={options.data.egitimTurleri.map((t) => ({
                value: t.id,
                label: t.grup ? `${t.ad} (${t.grup})` : t.ad,
              }))}
              value={turId}
              onChange={(deger) => {
                setTurId(deger);
                setKonular([]);
              }}
            />
            {/* ✏️ GEZİNTİ ARTÇISI 3 / B3 — NOT SÜRE SINIFINA BAKAR: `belge`
                sınıfı tür (ilk yardım) `yetkinlik_uretir` taşısa da yetkinlik
                DOĞURMAZ (bitiş belgenin üstünden, kişiye özel — E5); eski not
                bu türde yalan söylüyordu (§2.40 sunum kolu). */}
            {secilenTur?.yetkinlikUretir && secilenTur.sureSinifi !== 'belge' && (
              <Text size="xs" c="dimmed" data-testid="egitim-yetkinlik-notu">
                Bu eğitim başarıyla tamamlayan katılımcılara yetkinlik kaydı doğurur.
              </Text>
            )}
            {secilenTur?.yetkinlikUretir && secilenTur.sureSinifi === 'belge' && (
              <Text size="xs" c="dimmed" data-testid="egitim-yetkinlik-belge-notu">
                Bu türde yetkinlik eğitim kaydından doğmaz; sertifika tarihi kişiye özel olduğundan
                belge kişi kartından kaydedilir.
              </Text>
            )}
            <Group grow>
              {/* C2 (5½) — takvim seçici + `gunGirdisiIso` (madde 137 deseni). */}
              <TextInput
                label="Tarih"
                type="date"
                required
                data-testid="egitim-tarih"
                value={tarih}
                onChange={(event) => setTarih(event.currentTarget.value)}
                error={
                  tarih !== '' && gunGirdisiIso(tarih) === null
                    ? 'Geçerli bir gün seçin'
                    : undefined
                }
              />
              <NumberInput
                label="Süre (ders saati)"
                required
                min={0.5}
                step={0.5}
                decimalScale={1}
                data-testid="egitim-sure"
                value={sure}
                onChange={setSure}
              />
              <Select
                label="Yöntem"
                required
                data-testid="egitim-yontem"
                data={Object.entries(EGITIM_YONTEM_ETIKET).map(([value, label]) => ({
                  value,
                  label,
                }))}
                value={yontem}
                onChange={(deger) => setYontem(deger ?? 'yuz_yuze')}
              />
            </Group>
            <Checkbox
              label="Eğitici dışarıdan (OSGB / cihaz firması / dış kurum)"
              data-testid="egitim-dis-egitici"
              checked={disEgitici}
              onChange={(event) => setDisEgitici(event.currentTarget.checked)}
            />
            {disEgitici ? (
              <Group grow>
                <TextInput
                  label="Eğitici adı"
                  required
                  data-testid="egitim-egitici-ad"
                  value={egiticiAd}
                  onChange={(event) => setEgiticiAd(event.currentTarget.value)}
                />
                <TextInput
                  label="Eğitici kurumu"
                  description="Kurum AYRI alandır — ad ve kurum tek satıra yazılmaz"
                  data-testid="egitim-egitici-kurum"
                  value={egiticiKurum}
                  onChange={(event) => setEgiticiKurum(event.currentTarget.value)}
                />
                {/* İ4 — isteğe bağlı; "İBYS'ye bildirilemez" uyarısı YAZILMAZ (gönderim yok). */}
                <TextInput
                  label="Eğitici TC kimlik no (isteğe bağlı)"
                  maxLength={11}
                  data-testid="egitim-egitici-kimlik"
                  value={egiticiKimlikNo}
                  onChange={(event) => setEgiticiKimlikNo(event.currentTarget.value)}
                />
              </Group>
            ) : (
              <Select
                label="İç eğitici"
                required
                searchable
                data-testid="egitim-egitici-person"
                data={options.data.personeller.map((p) => ({ value: p.id, label: p.adSoyad }))}
                value={egiticiPersonId}
                onChange={setEgiticiPersonId}
              />
            )}
            {/* Müfredat işaretleri YALNIZ ders saati kuralı taşıyan türde
                (bugün temel İSG) sorulur — koşul SUNUCUDAN (`konuIster`). */}
            {secilenTur?.konuIster && (
              <MultiSelect
                label="İşlenen konular (temel İSG müfredatı)"
                description="Ek-1 dördüncü grup (işyerine özel riskler) konularını şirket kendi nüshasına ekler"
                searchable
                data-testid="egitim-konular"
                data={options.data.egitimKonulari.map((k) => ({
                  value: k.id,
                  label: k.grup ? `${k.ad} (${k.grup})` : k.ad,
                }))}
                value={konular}
                onChange={setKonular}
              />
            )}
            {/* C3 (5½) — detaydaki soluk satırla TEK desen (`egitim-parts.tsx`). */}
            <KatilimciSecici
              label="Katılımcılar"
              adaylar={options.data.personeller}
              value={katilimcilar}
              onChange={setKatilimcilar}
              testId="egitim-katilimcilar"
            />
            <Group grow>
              <Select
                label="Sorumlu İSG profesyoneli"
                searchable
                clearable
                data-testid="egitim-sorumlu"
                placeholder="Boşsa kaydı açan kişi"
                data={options.data.personeller.map((p) => ({ value: p.id, label: p.adSoyad }))}
                value={sorumluPersonId}
                onChange={setSorumluPersonId}
              />
              {options.data.egitimYerleri.length > 0 && (
                <Select
                  label="Eğitim yeri"
                  clearable
                  data-testid="egitim-yeri"
                  data={options.data.egitimYerleri.map((k) => ({ value: k.id, label: k.ad }))}
                  value={egitimYeriRefId}
                  onChange={setEgitimYeriRefId}
                />
              )}
              {options.data.sertifikaTipleri.length > 0 && (
                <Select
                  label="Sertifika tipi"
                  clearable
                  data-testid="egitim-sertifika-tipi"
                  data={options.data.sertifikaTipleri.map((k) => ({ value: k.id, label: k.ad }))}
                  value={sertifikaTipiRefId}
                  onChange={setSertifikaTipiRefId}
                />
              )}
            </Group>
            <Textarea
              label="Notlar"
              data-testid="egitim-notlar"
              value={notlar}
              onChange={(event) => setNotlar(event.currentTarget.value)}
            />
            {ac.isError && (
              <Alert color={ANLAM.engel} role="alert" data-testid="egitim-kayit-hata">
                {ac.error.message}
              </Alert>
            )}
            <Group>
              <Button
                data-testid="egitim-kaydet"
                disabled={!hazir}
                loading={ac.isPending}
                onClick={() => {
                  if (!hazir || !workplaceId || !turId) return;
                  ac.mutate(
                    {
                      workplaceId,
                      egitimTuruRefId: turId,
                      tarih,
                      sureDersSaati: Number(sure),
                      yontem: yontem as 'yuz_yuze' | 'online' | 'karma',
                      egiticiPersonId: disEgitici ? null : egiticiPersonId,
                      egiticiAd: disEgitici ? egiticiAd.trim() : null,
                      egiticiKurum:
                        disEgitici && egiticiKurum.trim() !== '' ? egiticiKurum.trim() : null,
                      egiticiKimlikNo:
                        disEgitici && egiticiKimlikNo.trim() !== '' ? egiticiKimlikNo.trim() : null,
                      // undefined → sunucu varsayılanı (kaydı açan); null göndermeyiz.
                      ...(sorumluPersonId ? { sorumluPersonId } : {}),
                      egitimYeriRefId,
                      sertifikaTipiRefId,
                      notlar: notlar.trim() === '' ? null : notlar.trim(),
                      konuRefIds: konular,
                      katilimcilar: katilimcilar.map((personId) => ({
                        personId,
                        taraf: yukleniciler.has(personId) ? 'yuklenici' : 'ana_firma',
                      })),
                    },
                    { onSuccess: (detay) => void navigate(`/egitimler/${detay.id}`) },
                  );
                }}
              >
                Kaydet
              </Button>
            </Group>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
